'use client';

import { Helmet } from 'react-helmet-async';
import { Link } from '@/components/shared/Link';
import { PageHero } from '@/components/shared/PageHero';
import { SectionLabel } from '@/components/shared/SectionLabel';
import { useTranslations } from '@/i18n/intl';
import { useSettings } from '@/components/shared/SettingsProvider';
import NotFoundPage from './NotFoundPage';

export default function SgdpPage() {
  const settings = useSettings();
  const isCareerEnabled = settings.enable_career !== 'false';
  const t = useTranslations('Common');

  if (!isCareerEnabled) return <NotFoundPage />;

  return (
    <>
      <Helmet>
        <title>Simisi Graduate Development Program (SGDP)</title>
        <meta
          name="description"
          content="A structured 12-month development program designed for proactive individuals who aspire to build a career in management consulting."
        />
      </Helmet>
      <PageHero
        title="SGDP"
        subtitle="Simisi Graduate Development Program"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: t('career') },
          { label: 'SGDP' },
        ]}
      />

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
        <div className="max-w-[800px] mx-auto">
          <SectionLabel text="Program" />
          <h2
            className="text-[32px] md:text-[40px] font-bold text-navy leading-snug mb-6 font-heading"
          >
            Simisi Graduate Development Program
          </h2>
          <p className="text-[16px] text-gray leading-relaxed mb-4 font-sans">
            Simisi Graduate Development Program (SGDP) returns for its third batch as a structured 12-month development program designed for proactive, responsible, and skilled individuals who aspire to build a career in management consulting.
          </p>
          <p className="text-[16px] text-gray leading-relaxed mb-4 font-sans">
            As an Associate Trainee, you will have the opportunity to grow and transform into a full-time Consultant at Simisi. Throughout the program, you will receive mentorship from senior consultants, hands-on project experience, and structured training to develop your consulting skills.
          </p>
          <p className="text-[16px] text-gray leading-relaxed font-sans">
            The SGDP is designed to accelerate your professional development and prepare you for a successful career in management consulting.
          </p>
        </div>
      </section>

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-light/30">
        <div className="max-w-[800px] mx-auto">
          <SectionLabel text="Requirements" />
          <h2
            className="text-[32px] md:text-[40px] font-bold text-navy leading-snug mb-8 font-heading"
          >
            Who we&apos;re looking for
          </h2>

          <div className="space-y-4">
            {[
              'Fresh graduate or final year student from a recognized university',
              'Minimum GPA of 3.30',
              'Strong analytical and communication skills',
              'Proactive and responsible attitude',
              'Passion for management consulting and problem-solving',
              'Ability to work in a fast-paced, team-oriented environment',
            ].map((req, index) => (
              <div key={index} className="flex items-start gap-3 bg-white rounded-lg p-4 shadow-sm border border-light">
                <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-full text-white font-bold text-[13px] shrink-0">
                  {index + 1}
                </div>
                <p className="text-[16px] text-gray leading-relaxed pt-1 font-sans">{req}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
        <div className="max-w-[800px] mx-auto text-center">
          <SectionLabel text="Benefits" className="text-center" />
          <h2
            className="text-[32px] md:text-[40px] font-bold text-navy leading-snug mb-12 font-heading"
          >
            What you&apos;ll gain
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Mentorship', desc: 'Work directly with experienced partners and leaders who guide your professional development.' },
              { title: 'Real Projects', desc: 'Gain hands-on experience working on real consulting projects from day one.' },
              { title: 'Career Growth', desc: 'Structured path from Associate Trainee to full-time Consultant within 12 months.' },
            ].map((benefit) => (
              <div key={benefit.title} className="bg-light/30 rounded-lg p-6 border border-light text-left">
                <h3 className="text-[18px] font-bold text-navy mb-3 font-heading">
                  {benefit.title}
                </h3>
                <p className="text-[14px] text-gray leading-relaxed font-sans">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-navy">
        <div className="max-w-[800px] mx-auto text-center">
          <h2
            className="text-[32px] md:text-[40px] font-bold text-white leading-snug mb-6 font-heading"
          >
            Ready to start your consulting career?
          </h2>
          <p className="text-[16px] text-white/80 leading-relaxed mb-8 font-sans">
            Apply now for the Simisi Graduate Development Program and take the first step toward a rewarding career in management consulting.
          </p>
          <Link
            href="/contact-us"
            className="inline-flex items-center bg-white text-navy text-[14px] font-semibold px-[25px] py-[11px] rounded-[25px] hover:bg-primary hover:text-white transition-all duration-300"
          >
            APPLY NOW
          </Link>
        </div>
      </section>
    </>
  );
}
